<template>
  <div>
    <StoreSwitcher v-if="isAdmin" />
    <b-card class="mb-3">
      <CommissionsReportFilter v-model="filter" @filter="loadReport" />
    </b-card>

    <b-card no-body class="mb-3">
      <b-card-header class="d-flex align-items-center justify-content-between">
        <div>
          <div class="font-weight-bold">Resumo do periodo</div>
          <small v-if="summaryOpen" class="text-muted">
            Somente alugueis com comissao
          </small>
        </div>
        <b-button
          v-b-toggle="summaryCollapseId"
          size="sm"
          variant="outline-secondary"
        >
          {{ summaryOpen ? 'Ocultar' : 'Mostrar' }}
        </b-button>
      </b-card-header>
      <b-collapse :id="summaryCollapseId" v-model="summaryOpen">
        <b-card-body>
          <StateRenderer
            :loading="commissionReport.loading"
            :error="commissionReport.error"
            :data="summaryItems"
          >
            <b-alert
              v-if="summaryItems.length === 0"
              show
              variant="secondary"
              class="text-center"
            >
              Nenhuma comissao registrada no periodo selecionado
            </b-alert>
            <template v-else>
              <b-row>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="text-muted small">Faturamento</div>
                  <div class="h5 m-0">
                    {{ summaryTotals.totalAmount | formatCurrency }}
                  </div>
                </b-col>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="text-muted small">Vendas Efetivas</div>
                  <div class="h5 m-0">{{ summaryTotals.totalSales }}</div>
                </b-col>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="text-muted small">Ticket medio</div>
                  <div class="h5 m-0">
                    {{ summaryTotals.averageTicket | formatCurrency }}
                  </div>
                </b-col>
                <b-col md="3" sm="6" class="mb-2">
                  <div class="text-muted small">Tempo medio</div>
                  <div class="h5 m-0">
                    {{ formatMinutes(summaryTotals.averagePeriodMinutes) }}
                  </div>
                </b-col>
              </b-row>
            </template>
          </StateRenderer>
        </b-card-body>
      </b-collapse>
    </b-card>

    <CommissionsRanking :report="commissionReport" :default-open="true" />
    <CommissionsLogList :report="commissionReport" :default-open="false" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'
import { formatDate } from '@/helpers/date'
import { formatCurrency } from '@/helpers/numbers'
import StoreSwitcher from '@/components/Stores/StoreSwitcher'
import StateRenderer from '@/components/StateRenderer'
import CommissionsReportFilter from './CommissionsReportFilter'
import CommissionsRanking from './CommissionsRanking'
import CommissionsLogList from './CommissionsLogList'

const startOfCurrentMonth = () => {
  const now = new Date()
  return formatDate(new Date(now.getFullYear(), now.getMonth(), 1))
}

export default {
  name: 'CommissionsReport',
  components: {
    CommissionsReportFilter,
    CommissionsRanking,
    CommissionsLogList,
    StoreSwitcher,
    StateRenderer
  },
  filters: { formatCurrency },
  mixins: [
    withAsyncAction({
      key: 'commissionReport',
      initialState: { logs: [], summary: [] },
      fetcher: {
        methodName: 'fetchCommissionsReport',
        handler: services.reports.fetchCommissionsReport
      }
    })
  ],
  data() {
    return {
      filter: {
        fromDate: startOfCurrentMonth(),
        toDate: formatDate(new Date())
      },
      summaryOpen: false
    }
  },
  computed: {
    ...mapGetters('store', ['currentStoreId', 'isAdmin']),
    summaryCollapseId() {
      return `commission-summary-${this._uid}`
    },
    summaryItems() {
      const data = this.commissionReport?.data || {}
      if (Array.isArray(data.summary)) return data.summary
      if (Array.isArray(data)) return data
      return []
    },
    summaryTotals() {
      const totals = {
        totalAmount: 0,
        totalSales: 0,
        averageTicket: 0,
        averagePeriodMinutes: 0
      }

      if (!this.summaryItems.length) return totals

      const totalAmount = this.summaryItems.reduce(
        (acc, item) => acc + (Number(item.totalAmount) || 0),
        0
      )
      const totalSales = this.summaryItems.reduce(
        (acc, item) => acc + (Number(item.totalSales) || 0),
        0
      )
      const weightedPeriodSum = this.summaryItems.reduce((acc, item) => {
        const sales = Number(item.totalSales) || 0
        const avgPeriod = Number(item.averagePeriodMinutes) || 0
        return acc + avgPeriod * sales
      }, 0)

      return {
        totalAmount,
        totalSales,
        averageTicket: totalSales ? totalAmount / totalSales : 0,
        averagePeriodMinutes: totalSales ? weightedPeriodSum / totalSales : 0
      }
    }
  },
  watch: {
    currentStoreId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadReport()
      }
    }
  },
  created() {
    this.loadReport()
  },
  methods: {
    formatMinutes(value) {
      const minutes = Number(value)
      if (!Number.isFinite(minutes) || minutes <= 0) return '0 min'
      const totalMinutes = Math.round(minutes)
      const hours = Math.floor(totalMinutes / 60)
      const rest = totalMinutes % 60
      if (hours > 0) {
        return `${hours}h ${rest}min`
      }
      return `${rest} min`
    },
    async loadReport() {
      const storeId = this.currentStoreId
      if (!storeId) return

      const { fromDate, toDate } = this.filter
      try {
        await this.fetchCommissionsReport({ storeId, fromDate, toDate })
      } catch (error) {
        // erros sao exibidos pelo StateRenderer
        console.error('Erro ao carregar relatorio de comissoes', error)
      }
    }
  }
}
</script>
